// Thin client for the Express backend (backend/server.js).
// Auth + dashboard content endpoints: login, register, profile, passwords, blogs.
import type { PermissionKey, Role, RoleAccess, Session } from "./types";

import { API_URL } from "@/lib/apiUrl";

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/** Shown once on the login page after the session was ended for a reason the user should know about. */
export const LOGIN_NOTICE_KEY = "mva.loginNotice";

interface ApiUser {
  id: number;
  portal_id: string | null;
  username: string;
  email: string;
  phone: string | null;
  name: string;
  role: Role;
  is_active: boolean;
  permissions: Partial<Record<PermissionKey, boolean>>;
  access?: RoleAccess | null;
  is_super_admin?: boolean;
  staff_role_name?: string | null;
}

interface AuthResponse {
  token: string;
  user: ApiUser;
  /** True only on a staff member's very first sign-in (password was auto-generated and emailed). */
  first_login?: boolean;
}

function toSession(data: AuthResponse): Session {
  return {
    token: data.token,
    role: data.user.role,
    name: data.user.name,
    id: data.user.portal_id ?? "", // learner id for students; staff have none
    email: data.user.email,
    permissions: data.user.permissions ?? {},
    access: data.user.access ?? null,
    isSuperAdmin: !!data.user.is_super_admin,
    roleName: data.user.staff_role_name ?? null,
  };
}

async function request<T>(
  path: string,
  opts: { method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"; body?: unknown; token?: string; form?: FormData } = {},
): Promise<T> {
  const isForm = !!opts.form;
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: opts.method ?? (opts.body || isForm ? "POST" : "GET"),
      headers: {
        ...(isForm ? {} : { "Content-Type": "application/json" }),
        ...(opts.token ? { Authorization: `Bearer ${opts.token}` } : {}),
      },
      body: isForm ? opts.form : opts.body ? JSON.stringify(opts.body) : undefined,
    });
  } catch {
    throw new ApiError(0, "Cannot reach the server. Check your connection and try again.");
  }
  const data = await res.json().catch(() => ({} as Record<string, unknown>));
  if (!res.ok) {
    const msg = (data as { message?: string; error?: string }).message ??
      (data as { error?: string }).error ??
      "Something went wrong. Please try again.";
    const code = (data as { code?: string }).code;
    // The account was disabled, or its password was changed elsewhere, while the person was signed in: end the session now, with an explanation.
    if (opts.token && (code === "ACCOUNT_DEACTIVATED" || code === "SESSION_EXPIRED") && typeof window !== "undefined") {
      try {
        window.localStorage.removeItem("mva.session");
        window.sessionStorage.removeItem("mva.session");
        window.sessionStorage.setItem(LOGIN_NOTICE_KEY, msg);
      } catch {
        /* storage blocked - the redirect below still happens */
      }
      window.location.assign("/login");
    }
    throw new ApiError(res.status, msg, code);
  }
  return data as T;
}

export async function apiLogin(input: {
  identifier: string;
  password: string;
  role: Role;
  rememberMe?: boolean;
}): Promise<{ session: Session; firstLogin: boolean }> {
  const data = await request<AuthResponse>("/auth/login", { body: input });
  return { session: toSession(data), firstLogin: !!data.first_login };
}

/** Validates the token and returns the user's latest role/permissions. */
export async function apiFetchProfile(token: string): Promise<Session> {
  const data = await request<AuthResponse>("/auth/profile", { token });
  return { ...toSession(data), token };
}

/** The signed-in user's own account details (read-only on the Profile page). */
export interface ProfileDetails {
  id: number;
  portal_id: string | null;
  username: string;
  email: string;
  phone: string | null;
  city: string | null;
  name: string;
  role: Role;
  createdAt: string;
}

export async function apiMyProfile(token: string): Promise<ProfileDetails> {
  const data = await request<{ user: ProfileDetails }>("/auth/profile", { token });
  return data.user;
}

/** Users can change only their own city. */
export async function apiUpdateMyProfile(token: string, input: { city?: string; username?: string }): Promise<ProfileDetails> {
  const data = await request<{ user: ProfileDetails }>("/auth/profile", { method: "PUT", body: input, token });
  return data.user;
}

export function apiChangePassword(token: string, currentPassword: string, newPassword: string) {
  return request<{ message: string; token?: string }>("/auth/change-password", {
    method: "POST",
    body: { currentPassword, newPassword },
    token,
  });
}

export function apiForgotPassword(identifier: string) {
  return request<{ message: string }>("/auth/forgot-password", { body: { identifier } });
}

export function apiResetPassword(
  token: string,
  email: string,
  newPassword: string,
  confirmPassword: string,
) {
  const q = `token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`;
  return request<{ message: string }>(`/auth/reset-password?${q}`, {
    method: "POST",
    body: { newPassword, confirmPassword },
  });
}

/* ------------------------------- Blogs -------------------------------- */

export interface PortalBlogPart {
  title: string | null;
  description: string | null;
  image: string | null;
}
export interface PortalBlogSection extends PortalBlogPart {
  subsections: PortalBlogPart[];
}
/** Older posts only. */
export interface PortalBlogBlock {
  type: "p" | "h2" | "ul";
  text?: string;
  items?: string[];
}

export interface PortalBlog {
  id: number;
  title: string;
  slug: string | null;
  description: string | null;
  excerpt: string | null;
  category: string | null;
  author: string | null;
  read_time: string | null;
  sort_order?: number;
  cover: string;
  /** Current posts: sections (image, title, description + sub-sections). Older posts: flat blocks. */
  sections: (PortalBlogSection | PortalBlogBlock)[];
  image: string | null;
  image_laptop?: string | null;
  image_desktop?: string | null;
  is_enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Uploads one image for a blog section / sub-section; returns its URL. */
export async function apiUploadBlogImage(token: string, file: File): Promise<{ path: string; url: string }> {
  const form = new FormData();
  form.append("image", file);
  const res = await request<{ data: { path: string; url: string } }>("/blogs/upload-image", { method: "POST", token, form });
  return res.data;
}

export async function apiBlogs(token: string): Promise<PortalBlog[]> {
  const data = await request<{ data: PortalBlog[] }>("/blogs?page_size=all", { token });
  return data.data ?? [];
}

export function apiCreateBlog(token: string, form: FormData) {
  return request<{ data: PortalBlog }>("/blogs", { method: "POST", token, form });
}

export function apiUpdateBlog(token: string, id: number, form: FormData) {
  return request<{ data: PortalBlog }>(`/blogs/${id}`, { method: "PUT", token, form });
}

export function apiDeleteBlog(token: string, id: number) {
  return request<{ message: string }>(`/blogs/${id}`, { method: "DELETE", token });
}

/* ------------------------------ Home video ----------------------------- */

export interface PortalHomeVideo {
  id: number;
  title: string | null;
  video_url: string;
  is_enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function apiHomeVideos(token: string): Promise<PortalHomeVideo[]> {
  const data = await request<{ data: PortalHomeVideo[] }>("/home-videos", { token });
  return data.data ?? [];
}

export function apiCreateHomeVideo(token: string, input: { title: string; video_url: string; is_enabled: boolean }) {
  return request<{ data: PortalHomeVideo }>("/home-videos", { method: "POST", body: input, token });
}

export function apiUpdateHomeVideo(token: string, id: number, input: Partial<PortalHomeVideo>) {
  return request<{ data: PortalHomeVideo }>(`/home-videos/${id}`, { method: "PUT", body: input, token });
}

export function apiDeleteHomeVideo(token: string, id: number) {
  return request<{ message: string }>(`/home-videos/${id}`, { method: "DELETE", token });
}

/* --------------------------- server-side paging ------------------------- */

/** Total / visible / hidden counts the list endpoints return next to a page (ignores search + paging). */
export interface EnabledSummary {
  total: number;
  enabled: number;
  disabled: number;
}

/**
 * One page of any list endpoint that follows the { data, pagination:{count}, summary? } shape.
 * `params` are extra filters (kind, status, course_id…); empty values are left out.
 */
export async function apiPage<T, X = EnabledSummary>(
  token: string,
  path: string,
  q: { page: number; pageSize: number; search: string },
  params: Record<string, string | number | boolean | null | undefined> = {},
): Promise<{ rows: T[]; total: number; extra?: X }> {
  const qs = new URLSearchParams({ page: String(q.page), page_size: String(q.pageSize) });
  if (q.search) qs.set("search", q.search);
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  const res = await request<{ data: T[]; pagination?: { count: number } | null; total?: number; summary?: X }>(`${path}?${qs}`, { token });
  const rows = res.data ?? [];
  return { rows, total: res.pagination?.count ?? res.total ?? rows.length, extra: res.summary };
}

/* -------------------------------- FAQs -------------------------------- */

export type FaqPage = "home" | "course" | "corporate_training";

export interface PortalFaq {
  id: number;
  question: string;
  answer: string;
  /** Pages it shows on (any combination): home = site-wide FAQ lists, course = that course's page, corporate_training = the Corporate Training page. */
  shown_on?: FaqPage[];
  /** Only set when page = "course". */
  course_id?: number | null;
  course_title?: string | null;
  sort_order?: number;
  is_enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export function apiCreateFaq(token: string, input: { question: string; answer: string; shown_on: FaqPage[]; course_id?: number | null; sort_order?: number; is_enabled: boolean }) {
  return request<{ data: PortalFaq }>("/faqs", { method: "POST", body: input, token });
}

export function apiUpdateFaq(token: string, id: number, input: Partial<PortalFaq>) {
  return request<{ data: PortalFaq }>(`/faqs/${id}`, { method: "PUT", body: input, token });
}

export function apiDeleteFaq(token: string, id: number) {
  return request<{ message: string }>(`/faqs/${id}`, { method: "DELETE", token });
}

/* ----------------------------- Course material ----------------------------- */

export type PortalMaterialKind = "brochure" | "ebook" | "video" | "other";

export interface PortalMaterial {
  id: number;
  course_id: number;
  title: string;
  description: string | null;
  kind: PortalMaterialKind;
  has_file: boolean;
  file_name: string | null;
  file_size: number | null;
  /** YouTube / Vimeo / other https link (when no file is uploaded). */
  link: string | null;
  sort_order: number;
  is_enabled: boolean;
}

export async function apiMaterials(token: string, courseId: number): Promise<PortalMaterial[]> {
  const res = await request<{ data: PortalMaterial[] }>(`/course-materials?course_id=${courseId}`, { token });
  return res.data ?? [];
}

export function apiAddMaterial(token: string, courseId: number, form: FormData) {
  return request<{ data: PortalMaterial }>(`/course-materials/course/${courseId}`, { method: "POST", form, token });
}

export function apiUpdateMaterial(token: string, id: number, form: FormData) {
  return request<{ data: PortalMaterial }>(`/course-materials/${id}`, { method: "PUT", form, token });
}

export function apiRemoveMaterial(token: string, id: number) {
  return request<{ message: string }>(`/course-materials/${id}/remove`, { method: "PUT", token });
}

/* ---------------------------------- Events ---------------------------------- */

export interface PortalEventPhoto {
  id: number;
  event_id: number;
  /** "video" items hold an uploaded file URL or a pasted YouTube / Vimeo / file link in `image`. */
  kind: "image" | "video";
  image: string;
  caption: string | null;
  sort_order: number;
}

export interface PortalEvent {
  id: number;
  title: string;
  slug: string;
  summary: string | null;
  description: string | null;
  /** YYYY-MM-DD */
  event_date: string | null;
  location: string | null;
  cover_image: string | null;
  cover_caption: string | null;
  /** Full URL of an uploaded file, or the pasted YouTube / Vimeo / file link. */
  video: string | null;
  video_caption: string | null;
  sort_order: number;
  is_enabled: boolean;
  media: PortalEventPhoto[];
  createdAt: string;
  updatedAt: string;
}

export function apiCreateEvent(token: string, form: FormData) {
  return request<{ data: PortalEvent }>("/events", { method: "POST", form, token });
}

export function apiUpdateEvent(token: string, id: number, form: FormData) {
  return request<{ data: PortalEvent }>(`/events/${id}`, { method: "PUT", form, token });
}

export function apiDeleteEvent(token: string, id: number) {
  return request<{ message: string }>(`/events/${id}`, { method: "DELETE", token });
}

/** Gallery calls are part of editing an event (they need the role's Update right). */
export function apiAddEventPhoto(token: string, eventId: number, form: FormData) {
  return request<{ data: PortalEventPhoto }>(`/events/${eventId}/media`, { method: "PUT", form, token });
}

export function apiRemoveEventPhoto(token: string, eventId: number, photoId: number) {
  return request<{ message: string }>(`/events/${eventId}/media/${photoId}/remove`, { method: "PUT", token });
}

/* ------------------------------- Hero banners ------------------------------- */

export interface PortalHeroSlide {
  id: number;
  /** Page the slide belongs to, e.g. "/" or "/about". */
  page_path: string;
  title: string | null;
  description: string | null;
  alt_text: string | null;
  /** Seconds on screen before the next slide. */
  duration: number;
  is_enabled: boolean;
  /** Images per screen size; the Nth image of each size makes up the Nth slide. */
  images: { desktop: string[]; laptop: string[]; tablet: string[]; mobile: string[] };
  createdAt: string;
  updatedAt: string;
}

export function apiCreateHeroSlide(token: string, form: FormData) {
  return request<{ data: PortalHeroSlide }>("/hero-slides", { method: "POST", form, token });
}

export function apiUpdateHeroSlide(token: string, id: number, form: FormData) {
  return request<{ data: PortalHeroSlide }>(`/hero-slides/${id}`, { method: "PUT", form, token });
}

export function apiDeleteHeroSlide(token: string, id: number) {
  return request<{ message: string }>(`/hero-slides/${id}`, { method: "DELETE", token });
}

/* ----------------------------- SEO & meta tags ----------------------------- */

export interface PortalSeoPage {
  id: number;
  /** Route the settings apply to, e.g. "/about" or "/courses/ielts-preparation-course". */
  path: string;
  name: string | null;
  title: string;
  description: string;
  keywords: string | null;
  canonical: string | null;
  og_image: string | null;
  noindex: boolean;
  /** true = no brand suffix after the title. */
  absolute_title: boolean;
  content_heading: string | null;
  content_body: string | null;
  is_enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export type SeoPageInput = Omit<PortalSeoPage, "id" | "createdAt" | "updatedAt">;

/** Uploads a social share image; returns its full URL. */
export async function apiUploadSeoImage(token: string, file: File): Promise<string> {
  const form = new FormData();
  form.append("image", file);
  const res = await request<{ data: { path: string; url: string } }>("/seo-pages/upload-image", { method: "POST", token, form });
  return res.data.url;
}

export function apiCreateSeoPage(token: string, input: SeoPageInput) {
  return request<{ data: PortalSeoPage }>("/seo-pages", { method: "POST", body: input, token });
}

export function apiUpdateSeoPage(token: string, id: number, input: Partial<SeoPageInput>) {
  return request<{ data: PortalSeoPage }>(`/seo-pages/${id}`, { method: "PUT", body: input, token });
}

export function apiDeleteSeoPage(token: string, id: number) {
  return request<{ message: string }>(`/seo-pages/${id}`, { method: "DELETE", token });
}

export interface PortalStat {
  id: number;
  label: string;
  value: number;
  suffix: string | null;
  sort_order: number;
  is_enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function apiStats(token: string): Promise<PortalStat[]> {
  const data = await request<{ data: PortalStat[] }>("/stats?page_size=all", { token });
  return data.data ?? [];
}

export function apiCreateStat(token: string, input: { label: string; value: number; suffix: string; sort_order: number; is_enabled: boolean }) {
  return request<{ data: PortalStat }>("/stats", { method: "POST", body: input, token });
}

export function apiUpdateStat(token: string, id: number, input: Partial<PortalStat>) {
  return request<{ data: PortalStat }>(`/stats/${id}`, { method: "PUT", body: input, token });
}

export function apiDeleteStat(token: string, id: number) {
  return request<{ message: string }>(`/stats/${id}`, { method: "DELETE", token });
}

export interface PortalTeamMember {
  id: number;
  name: string;
  designation: string | null;
  experience: string | null;
  expertise: string | null;
  bio: string | null;
  photo: string | null;
  sort_order: number;
  is_enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function apiTeam(token: string): Promise<PortalTeamMember[]> {
  const data = await request<{ data: PortalTeamMember[] }>("/team?page_size=all", { token });
  return data.data ?? [];
}

export function apiCreateTeamMember(token: string, form: FormData) {
  return request<{ data: PortalTeamMember }>("/team", { method: "POST", form, token });
}

export function apiUpdateTeamMember(token: string, id: number, form: FormData) {
  return request<{ data: PortalTeamMember }>(`/team/${id}`, { method: "PUT", form, token });
}

export function apiDeleteTeamMember(token: string, id: number) {
  return request<{ message: string }>(`/team/${id}`, { method: "DELETE", token });
}

export interface PortalTestimonial {
  id: number;
  title: string | null;
  description: string;
  name: string | null;
  role: string | null;
  company: string | null;
  course: string | null;
  rating: number;
  image: string | null;
  video: string | null;
  sort_order: number;
  is_enabled: boolean;
  /** Also shown on the home page (testimonials without a course always are). */
  show_on_home?: boolean;
  /** Set for testimonials written by a student; those can't be edited. */
  user_id?: number | null;
  status?: "pending" | "approved";
  createdAt: string;
  updatedAt: string;
}

export async function apiTestimonials(token: string): Promise<PortalTestimonial[]> {
  const data = await request<{ data: PortalTestimonial[] }>("/testimonials?page_size=all", { token });
  return data.data ?? [];
}

export async function apiMyTestimonials(token: string): Promise<{ data: PortalTestimonial[]; courses: { id: number; title: string }[] }> {
  const res = await request<{ data: PortalTestimonial[]; courses: { id: number; title: string }[] }>("/testimonials/mine", { token });
  return { data: res.data ?? [], courses: res.courses ?? [] };
}

export function apiDeleteMyTestimonial(token: string, id: number) {
  return request<{ message: string }>(`/testimonials/mine/${id}`, { method: "DELETE", token });
}

export function apiCreateMyTestimonial(token: string, form: FormData) {
  return request<{ message: string; data: PortalTestimonial }>("/testimonials/mine", { method: "POST", form, token });
}

export function apiCreateTestimonial(token: string, form: FormData) {
  return request<{ data: PortalTestimonial }>("/testimonials", { method: "POST", form, token });
}

export function apiUpdateTestimonial(token: string, id: number, form: FormData) {
  return request<{ data: PortalTestimonial }>(`/testimonials/${id}`, { method: "PUT", form, token });
}

export function apiDeleteTestimonial(token: string, id: number) {
  return request<{ message: string }>(`/testimonials/${id}`, { method: "DELETE", token });
}

/* -------------------------- Master categories -------------------------- */

export interface PortalMasterCategory {
  id: number;
  name: string;
  sort_order: number;
  is_enabled: boolean;
  show_in_footer: boolean;
  footer_sort_order: number;
  createdAt: string;
  updatedAt: string;
}

export async function apiCategories(token: string): Promise<PortalMasterCategory[]> {
  const data = await request<{ data: PortalMasterCategory[] }>("/master-categories?page_size=all", { token });
  return data.data ?? [];
}

export function apiCreateCategory(token: string, input: { name: string; sort_order: number; is_enabled: boolean }) {
  return request<{ data: PortalMasterCategory }>("/master-categories", { method: "POST", body: input, token });
}

export function apiUpdateCategory(token: string, id: number, input: Partial<PortalMasterCategory>) {
  return request<{ data: PortalMasterCategory }>(`/master-categories/${id}`, { method: "PUT", body: input, token });
}

export function apiDeleteCategory(token: string, id: number) {
  return request<{ message: string }>(`/master-categories/${id}`, { method: "DELETE", token });
}

/* ------------------------------- Clients ------------------------------- */

export interface PortalClient {
  id: number;
  name: string;
  sector: string | null;
  category_id: number | null;
  category_name: string | null;
  image: string | null;
  sort_order: number;
  is_enabled: boolean;
  is_corporate: boolean;
  show_on_home?: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function apiClients(token: string): Promise<PortalClient[]> {
  const data = await request<{ data: PortalClient[] }>("/clients?page_size=all", { token });
  return data.data ?? [];
}

export function apiCreateClient(token: string, form: FormData) {
  return request<{ data: PortalClient }>("/clients", { method: "POST", form, token });
}

export function apiUpdateClient(token: string, id: number, form: FormData) {
  return request<{ data: PortalClient }>(`/clients/${id}`, { method: "PUT", form, token });
}

export function apiDeleteClient(token: string, id: number) {
  return request<{ message: string }>(`/clients/${id}`, { method: "DELETE", token });
}

/* ---------------------------- Certifications ---------------------------- */

export interface PortalCertification {
  id: number;
  title: string;
  issuer: string | null;
  note: string | null;
  /** Optional certificate picture (full URL). */
  image: string | null;
  /** null = shown on the About page; otherwise on that course's page. */
  course_id: number | null;
  course_title: string | null;
  sort_order: number;
  is_enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function apiCertifications(token: string): Promise<PortalCertification[]> {
  const data = await request<{ data: PortalCertification[] }>("/certifications?page_size=all", { token });
  return data.data ?? [];
}

export function apiCreateCertification(token: string, form: FormData) {
  return request<{ data: PortalCertification }>("/certifications", { method: "POST", form, token });
}

/** Pass FormData to change the image (multipart); a plain object for simple field changes such as the enable toggle. */
export function apiUpdateCertification(token: string, id: number, input: Partial<PortalCertification> | FormData) {
  return input instanceof FormData
    ? request<{ data: PortalCertification }>(`/certifications/${id}`, { method: "PUT", form: input, token })
    : request<{ data: PortalCertification }>(`/certifications/${id}`, { method: "PUT", body: input, token });
}

export function apiDeleteCertification(token: string, id: number) {
  return request<{ message: string }>(`/certifications/${id}`, { method: "DELETE", token });
}

/* -------------------------------- Courses -------------------------------- */

export interface PortalCourse {
  id: number;
  material_count?: number;
  title: string;
  slug: string;
  tagline: string | null;
  category_id: number | null;
  category_name: string | null;
  level: string | null;
  duration: string | null;
  mode: string | null;
  summary: string | null;
  description: string[] | null;
  image: string | null;
  accent: string | null;
  fee: number | null;
  gstNote: string | null;
  showFee: boolean;
  featured: boolean;
  rating: number | null;
  enrolled: number | null;
  instructorSlug: string | null;
  outcomes: string[] | null;
  learn: string[] | null;
  learnIntro: string | null;
  modules: { title: string; desc?: string; topics?: string[] }[] | null;
  projects: string[] | null;
  certification: string | null;
  certifications: { title: string; desc: string }[] | null;
  batches: unknown[] | null;
  faqs: { q: string; a: string }[] | null;
  whyTitle: string | null;
  why: string[] | null;
  coreSkills: string | null;
  eligibility: string | null;
  prerequisites: string | null;
  batchNote: string | null;
  stats: { value: string; label: string }[] | null;
  careerAreas: { title: string; desc: string }[] | null;
  careersIntro: string | null;
  careersNote: string | null;
  careers: string[] | null;
  enquiryForm: string | null;
  gallery?: string[] | null;
  weekday_batch?: boolean;
  weekend_batch?: boolean;
  sort_order: number;
  is_enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function apiCourses(token: string): Promise<PortalCourse[]> {
  const data = await request<{ data: PortalCourse[] }>("/courses?page_size=all", { token });
  return data.data ?? [];
}

export function apiCreateCourse(token: string, form: FormData) {
  return request<{ data: PortalCourse }>("/courses", { method: "POST", form, token });
}

export function apiUpdateCourse(token: string, id: number, form: FormData) {
  return request<{ data: PortalCourse }>(`/courses/${id}`, { method: "PUT", form, token });
}

export function apiDeleteCourse(token: string, id: number) {
  return request<{ message: string }>(`/courses/${id}`, { method: "DELETE", token });
}

/* ------------------------------ Activity log ------------------------------ */

export interface PortalAuditLog {
  id: number;
  user_id: number | null;
  user_name: string | null;
  user_email?: string | null;
  user_role: string | null;
  action: string;
  entity: string;
  record_id: number | null;
  label: string | null;
  changes: string | null;
  method: string;
  path: string;
  status_code: number;
  success: boolean;
  ip: string | null;
  createdAt: string;
}

export interface PortalAuditUser {
  user_id: number;
  user_name: string | null;
  entries: number;
}

export interface AuditLogFilters {
  userId?: string;
  action?: string;
  entity?: string;
  dateFrom?: string;
  dateTo?: string;
}

export async function apiAuditLogs(token: string, filters: AuditLogFilters = {}): Promise<PortalAuditLog[]> {
  const params = new URLSearchParams({ page_size: "all", export: "1" });
  if (filters.userId) params.set("user_id", filters.userId);
  if (filters.action) params.set("action", filters.action);
  if (filters.entity) params.set("entity", filters.entity);
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  const data = await request<{ data: PortalAuditLog[] }>(`/audit-logs?${params.toString()}`, { token });
  return data.data ?? [];
}

export async function apiAuditLogUsers(token: string): Promise<PortalAuditUser[]> {
  const data = await request<{ data: PortalAuditUser[] }>("/audit-logs/users", { token });
  return data.data ?? [];
}

export function apiDeleteAuditLog(token: string, id: number) {
  return request<{ message: string }>(`/audit-logs/${id}`, { method: "DELETE", token });
}

export function apiClearAuditLogs(token: string) {
  return request<{ message: string }>("/audit-logs/all", { method: "DELETE", token });
}

/* ---------------------------- enquiries ---------------------------- */

export interface PortalEnquiry {
  id: number;
  /** Public reference number, e.g. ENQ10065. */
  reference?: string | null;
  kind: string;
  name: string;
  phone: string;
  email: string;
  city: string | null;
  interestedCourse: string | null;
  programme: string | null;
  programmes: string[] | null;
  batch: string | null;
  message: string | null;
  company: string | null;
  designation: string | null;
  location: string | null;
  mode: string | null;
  requirement: string | null;
  consent: boolean;
  source: string;
  status: string;
  notes: string | null;
  /** Set when the enquiry is a student's enrolment request. */
  course_id?: number | null;
  /** Set once staff approved or rejected the enquiry. */
  decided_at?: string | null;
  batch_id?: number | null;
  decision_note?: string | null;
  decided_by?: string | null;
  /** Added for the student's own list. */
  batch_name?: string | null;
  course_title?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Every enquiry matching the filters (used for the Excel export); the table itself pages with apiPage. */
export async function apiEnquiries(token: string, params: Record<string, string> = {}): Promise<PortalEnquiry[]> {
  const qs = new URLSearchParams({ page_size: "all", export: "1" });
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
  const data = await request<{ data: PortalEnquiry[] }>(`/enquiries?${qs}`, { token });
  return data.data ?? [];
}

/** Bulk upload of enquiries parsed from an Excel sheet (needs the role's Import right). */
export async function apiImportEnquiries(
  token: string,
  rows: Record<string, string>[],
): Promise<{ message: string; created: number; skipped: number; errors: { row: number; message: string }[] }> {
  const res = await request<{ message: string; data: { created: number; skipped: number; errors: { row: number; message: string }[] } }>(
    "/enquiries/import",
    { method: "POST", body: { rows }, token },
  );
  return { message: res.message, ...res.data };
}

export function apiUpdateEnquiry(token: string, id: number, input: { status?: string; notes?: string; name?: string; email?: string; phone?: string; city?: string; programme?: string; programmes?: string[]; message?: string }) {
  return request<{ message: string }>(`/enquiries/${id}`, { method: "PUT", body: input, token });
}

export interface EnquiryHistoryEntry {
  id: number;
  action: "received" | "status" | "notes" | "approved" | "rejected";
  actor_name: string | null;
  actor_role: string | null;
  from_value: string | null;
  to_value: string | null;
  note: string | null;
  createdAt: string;
}

export interface EnquiryHistorySummary {
  reference: string | null;
  kind: string;
  source: string;
  status: string;
  submitted_at: string;
  name: string;
  email: string;
  phone: string;
  city: string | null;
  company: string | null;
  interested_course: string | null;
  requested_courses: string[];
  preferred_batch: string | null;
  message: string | null;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  student: { name: string; portal_id: string | null; email: string } | null;
  enrolments: { course_title: string | null; batch_name: string; batch_code: string; mode: string; timing: string | null; mentor: string | null; start_date: string | null; enrolled_at: string; enrolled_by: string | null }[];
}

export async function apiEnquiryHistory(token: string, id: number): Promise<{ entries: EnquiryHistoryEntry[]; summary: EnquiryHistorySummary | null }> {
  const data = await request<{ data: EnquiryHistoryEntry[]; summary?: EnquiryHistorySummary | null }>(`/enquiries/${id}/history`, { token });
  return { entries: data.data ?? [], summary: data.summary ?? null };
}

export function apiDeleteEnquiry(token: string, id: number) {
  return request<{ message: string }>(`/enquiries/${id}`, { method: "DELETE", token });
}


export function apiDeleteMyEnquiry(token: string, id: number) {
  return request<{ message: string }>(`/enquiries/mine/${id}`, { method: "DELETE", token });
}

/* ---------------------------------------------------- dashboard summary */

export interface MixDatum {
  name: string;
  value: number;
}

export interface StaffDashboard {
  role: "employee";
  /** Which modules the viewer may read - everything else is returned empty and should be hidden. */
  visible?: { students: boolean; courses: boolean; blogs: boolean; faqs: boolean; enquiries: boolean; staff: boolean; reports: boolean };
  students: number;
  staffUsers: number;
  coursesLive: number;
  blogsLive: number;
  faqsLive: number;
  enquiries: number;
  enquiriesWeek: number;
  signupsWeek: number;
  activityWeek: number;
  statusMix: MixDatum[];
  kindMix: MixDatum[];
  monthly: { month: string; enquiries: number; students: number }[];
  week: { day: string; done: number; failed: number }[];
  recentEnquiries: { id: number; kind: string; name: string; phone: string; status: string; createdAt: string }[];
  recentActivity: { id: number; actor: string; action: string; target: string; time: string }[];
}

export interface StudentDashboard {
  role: "student";
  total: number;
  awaiting: number;
  converted: number;
  lost: number;
  coursesLive: number;
  statusMix: MixDatum[];
  recentEnquiries: { id: number; kind: string; name: string; status: string; createdAt: string }[];
}

export type DashboardData = StaffDashboard | StudentDashboard;

export async function apiDashboardSummary(token: string): Promise<DashboardData> {
  const data = await request<{ data: DashboardData }>("/dashboard/summary", { token });
  return data.data;
}

/* ---------------------------------------------------------------- staff */

export interface PortalUser {
  id: number;
  portal_id: string | null;
  username: string;
  email: string;
  phone: string | null;
  name: string;
  role: Role;
  is_active: boolean;
  staff_role_id: number | null;
  /** true when the account holds the Super Admin system role (set by GET /users). */
  is_super_admin?: boolean;
  createdAt: string;
  updatedAt: string;
  is_client: boolean;
  is_testimonial: boolean;
  is_home_video: boolean;
  is_certification: boolean;
  is_blogs: boolean;
  is_faq: boolean;
  is_seo: boolean;
  is_hero: boolean;
  is_event: boolean;
  is_stats: boolean;
  is_team: boolean;
  is_user: boolean;
  is_contact: boolean;
  is_category: boolean;
  is_student: boolean;
  is_course: boolean;
  is_course_manage: boolean;
  is_attendance: boolean;
  is_report: boolean;
  is_role: boolean;
  is_activity: boolean;
  is_fee: boolean;
}

export interface StaffInput {
  name: string;
  email: string;
  username?: string;
  phone?: string;
  role?: Role;
  /** Staff role (see Roles page) that decides which modules and CRUD actions they get. */
  role_id?: number | null;
}

// All boolean permission flags a staff account can hold (matches backend PERMISSION_KEYS).
export const STAFF_PERMISSIONS: { key: PermissionKey; label: string; menus: string }[] = [
  { key: "is_course", label: "Courses & batches", menus: "Courses, Batches" },
  { key: "is_category", label: "Course categories", menus: "Course Categories" },
  { key: "is_certification", label: "Certifications", menus: "Certifications" },
  { key: "is_contact", label: "Leads & enquiries", menus: "Enquiries" },
  { key: "is_student", label: "Students", menus: "Students" },
  { key: "is_team", label: "Mentors", menus: "Mentors" },
  { key: "is_user", label: "Manage staff", menus: "Staff" },
  { key: "is_role", label: "Roles", menus: "Roles" },
  { key: "is_activity", label: "Activity log", menus: "Activity Log" },
  { key: "is_blogs", label: "Blogs", menus: "Blogs" },
  { key: "is_faq", label: "FAQs", menus: "FAQs" },
  { key: "is_seo", label: "SEO & meta tags", menus: "SEO & Meta Tags" },
  { key: "is_hero", label: "Hero banners", menus: "Hero Banners" },
  { key: "is_event", label: "Events", menus: "Events" },
  { key: "is_stats", label: "Stats", menus: "Stats" },
  { key: "is_testimonial", label: "Testimonials", menus: "Testimonials" },
  { key: "is_home_video", label: "Home video", menus: "Home Video" },
  { key: "is_client", label: "Clients", menus: "Clients" },
  { key: "is_report", label: "Reports", menus: "Reports" },
  // Hidden for now - Attendance register / Fee collection
];

export async function apiUsers(token: string): Promise<PortalUser[]> {
  const data = await request<{ data: PortalUser[] }>("/users?page_size=all", { token });
  return data.data ?? [];
}

export function apiCreateUser(token: string, input: StaffInput) {
  return request<{ message: string; mail_sent: boolean; data: PortalUser }>("/users", { body: input, token });
}

export function apiUpdateUser(token: string, id: number, input: Partial<StaffInput>) {
  return request<{ message: string; data: PortalUser }>(`/users/${id}`, { method: "PUT", body: input, token });
}

export function apiToggleUserStatus(token: string, id: number, is_active: boolean) {
  return request<{ message: string; data: PortalUser }>(`/users/${id}/status`, { method: "PATCH", body: { is_active }, token });
}

export function apiDeleteUser(token: string, id: number) {
  return request<{ message: string }>(`/users/${id}`, { method: "DELETE", token });
}

/* ---------------------------------------------------------------- roles */

export type CrudAction = "create" | "read" | "update" | "delete" | "export" | "import";
export const CRUD_ACTIONS: { key: CrudAction; label: string }[] = [
  { key: "create", label: "Create" },
  { key: "read", label: "Read" },
  { key: "update", label: "Update" },
  { key: "delete", label: "Delete" },
];

/** Modules that have an Export button - the role editor only offers the Export switch for these. */
export const EXPORTABLE_MODULES: PermissionKey[] = ["is_contact", "is_activity"];
export const EXPORT_ACTION: { key: CrudAction; label: string } = { key: "export", label: "Export" };
/** Modules that have an Import button. */
export const IMPORTABLE_MODULES: PermissionKey[] = ["is_contact"];
export const IMPORT_ACTION: { key: CrudAction; label: string } = { key: "import", label: "Import" };

export type RolePermissions = Partial<Record<PermissionKey, Partial<Record<CrudAction, boolean>>>>;

export interface PortalRole {
  id: number;
  name: string;
  is_active: boolean;
  is_system: boolean;
  created_by: string | null;
  permissions: RolePermissions;
  user_count: number;
  createdAt: string;
  updatedAt: string;
}

export interface RoleInput {
  name: string;
  is_active: boolean;
  permissions: RolePermissions;
}

export async function apiRoles(token: string): Promise<PortalRole[]> {
  const data = await request<{ data: PortalRole[] }>("/roles", { token });
  return data.data ?? [];
}

export function apiCreateRole(token: string, input: RoleInput) {
  return request<{ message: string; data: PortalRole }>("/roles", { body: input, token });
}

export function apiUpdateRole(token: string, id: number, input: Partial<RoleInput>) {
  return request<{ message: string; data: PortalRole }>(`/roles/${id}`, { method: "PUT", body: input, token });
}

export function apiDeleteRole(token: string, id: number) {
  return request<{ message: string }>(`/roles/${id}`, { method: "DELETE", token });
}

/* ---------------------------------------------------------------- batches */

export type BatchMode = "Online" | "Offline" | "Hybrid";
export type BatchStatusValue = "Upcoming" | "Ongoing" | "Completed";

export interface PortalBatch {
  id: number;
  code: string; // B-01
  name: string;
  course_id: number | null;
  course_title: string | null;
  mentor: string | null;
  mode: BatchMode;
  timing: string | null;
  start_date: string | null; // YYYY-MM-DD
  seats: number;
  enrolled: number;
  status: BatchStatusValue;
}

export interface BatchInput {
  name: string;
  course_id: number | null;
  mentor: string;
  mode: BatchMode;
  timing: string;
  start_date: string | null;
  seats: number;
  status: BatchStatusValue;
}

export function apiCreateBatch(token: string, input: BatchInput) {
  return request<{ message: string; data: PortalBatch }>("/batches", { body: input, token });
}

export function apiUpdateBatch(token: string, id: number, input: BatchInput) {
  return request<{ message: string; data: PortalBatch }>(`/batches/${id}`, { method: "PUT", body: input, token });
}

export function apiDeleteBatch(token: string, id: number) {
  return request<{ message: string }>(`/batches/${id}`, { method: "DELETE", token });
}

/* ---------------------------------------------------------------- reports */

export interface ReportFilters {
  months?: number;
  status?: string;
  source?: string;
  kind?: string;
  course_id?: string;
  batch_status?: string;
}

export interface ReportData {
  months: number;
  options?: {
    statuses: string[];
    sources: string[];
    kinds: { value: string; label: string }[];
    courses: { value: number; label: string }[];
    batchStatuses: string[];
  };
  visible?: { enquiries: boolean; students: boolean; courses: boolean };
  kpis: {
    enquiries: number;
    enquiriesPeriod: number;
    conversionRate: number;
    converted: number;
    students: number;
    studentsPeriod: number;
    staff: number;
    coursesLive: number;
    batchesOngoing: number;
    seatUtilisation: number;
    seats: number;
    enrolled: number;
  };
  monthly: { month: string; enquiries: number; converted: number; students: number }[];
  statusMix: MixDatum[];
  kindMix: MixDatum[];
  sourceMix: MixDatum[];
  categoryMix: MixDatum[];
  batches: {
    id: number;
    code: string;
    name: string;
    course: string | null;
    mentor: string | null;
    status: string;
    seats: number;
    enrolled: number;
    utilisation: number;
  }[];
}

export async function apiReportSummary(token: string, filters: ReportFilters = {}): Promise<ReportData> {
  const qs = new URLSearchParams({ months: String(filters.months ?? 12) });
  for (const [k, v] of Object.entries(filters)) if (k !== "months" && v) qs.set(k, String(v));
  const data = await request<{ data: ReportData }>(`/reports/summary?${qs}`, { token });
  return data.data;
}

/* ---------------------------------------------------------------- notifications */

export interface PortalNotification {
  id: number;
  user_id: number;
  actor_id: number | null;
  actor_name: string | null;
  actor_role: string | null;
  type: string; // CREATE | UPDATE | DELETE | LOGIN | REGISTER | PASSWORD | ENQUIRY | INFO
  title: string;
  message: string | null;
  entity: string | null;
  record_id: number | null;
  is_read: boolean;
  createdAt: string;
}

export interface NotificationList {
  data: PortalNotification[];
  unread: number;
  total: number;
  page: number;
  total_pages: number;
  /** True for admins - they may send notifications to others. */
  can_send: boolean;
}

export function apiNotifications(token: string, opts: { unread?: boolean; page?: number; pageSize?: number; search?: string } = {}) {
  const q = new URLSearchParams();
  if (opts.unread) q.set("unread", "1");
  if (opts.page) q.set("page", String(opts.page));
  if (opts.pageSize) q.set("page_size", String(opts.pageSize));
  if (opts.search) q.set("search", opts.search);
  return request<NotificationList>(`/notifications?${q.toString()}`, { token });
}

export async function apiNotificationUnread(token: string): Promise<number> {
  const data = await request<{ unread: number }>("/notifications/unread-count", { token });
  return data.unread ?? 0;
}

export type NotificationAudience = "all" | "students" | "staff" | "user";

export function apiSendNotification(
  token: string,
  input: { title: string; message: string; audience: NotificationAudience; user_id?: number },
) {
  return request<{ message: string; sent: number }>("/notifications", { body: input, token });
}

export function apiUpdateNotification(token: string, id: number, input: { is_read?: boolean; title?: string; message?: string }) {
  return request<{ data: PortalNotification }>(`/notifications/${id}`, { method: "PUT", body: input, token });
}

export function apiMarkAllNotificationsRead(token: string) {
  return request<{ message: string }>("/notifications/read-all", { method: "PATCH", token });
}

export function apiDeleteNotification(token: string, id: number) {
  return request<{ message: string }>(`/notifications/${id}`, { method: "DELETE", token });
}

export function apiClearNotifications(token: string, onlyRead = false) {
  return request<{ message: string }>(`/notifications${onlyRead ? "?read=1" : ""}`, { method: "DELETE", token });
}

/* ---------------------------------------------------------------- batch enrolment */

export interface BatchStudent {
  id: number;
  name: string;
  email: string;
  portal_id: string | null;
  phone: string | null;
  enrolled_on?: string;
  enquiry_reference?: string | null;
}

export interface BatchStudents {
  /** Every course module is ticked off - certificates can be uploaded. */
  course_complete?: boolean;
  enrolled: (BatchStudent & { enrolled_on?: string; certificate?: StudentCertificate | null })[];
  available: BatchStudent[];
}

export async function apiBatchStudents(token: string, batchId: number): Promise<BatchStudents> {
  const data = await request<{ data: BatchStudents }>(`/batches/${batchId}/students`, { token });
  return data.data;
}

export function apiEnrollStudents(token: string, batchId: number, userIds: number[]) {
  return request<{ message: string; data: PortalBatch }>(`/batches/${batchId}/enroll`, { body: { user_ids: userIds }, token });
}

export function apiUnenrollStudent(token: string, batchId: number, userId: number) {
  return request<{ message: string; data: PortalBatch }>(`/batches/${batchId}/students/${userId}`, { method: "DELETE", token });
}

/* ---------------------------------------------------------------- students */

export interface StudentEnrollment {
  batch_id: number;
  batch_name: string;
  batch_code: string;
  course_id: number | null;
  course_title: string | null;
  status: string;
  mode: string;
  timing: string | null;
  progress?: { percent: number; modules: { title: string; done: boolean }[]; completed_at: string | null };
  mentor?: string | null;
  start_date?: string | null;
  enrolled_at?: string;
  enrolled_by?: string | null;
  /** The enquiry this student came in through. */
  enquiry_reference?: string | null;
  enquiry_kind?: string | null;
  enquiry_source?: string | null;
  enquiry_date?: string | null;
}

export interface StudentRecord {
  id: number;
  portal_id: string | null;
  name: string;
  email: string;
  phone: string | null;
  city: string | null;
  is_active: boolean;
  createdAt: string;
  /** Courses asked about in an enquiry that the student is not placed in yet. */
  pending_courses?: { course_title: string; enquiry_reference: string | null; enquiry_date: string | null }[];
  enrollments: StudentEnrollment[];
}

export interface StudentOptions {
  courses: { id: number; title: string }[];
  batches: {
    id: number;
    code: string;
    name: string;
    course_id: number;
    mode: string;
    timing: string | null;
    status: string;
    seats: number;
    enrolled: number;
  }[];
}

export interface StudentInput {
  name: string;
  email: string;
  phone: string;
  city: string;
  is_active?: boolean;
  /** One batch per course the student is joining - the full set (edits replace it). */
  batch_ids: number[];
}

export async function apiStudents(token: string): Promise<StudentRecord[]> {
  const data = await request<{ data: StudentRecord[] }>("/students", { token });
  return data.data ?? [];
}

export async function apiStudentOptions(token: string): Promise<StudentOptions> {
  const data = await request<{ data: StudentOptions }>("/students/options", { token });
  return data.data;
}

export function apiCreateStudent(token: string, input: StudentInput) {
  return request<{ message: string; mail_sent: boolean; data: StudentRecord }>("/students", { body: input, token });
}

export function apiUpdateStudent(token: string, id: number, input: StudentInput) {
  return request<{ message: string; data: StudentRecord }>(`/students/${id}`, { method: "PUT", body: input, token });
}

export function apiDeleteStudent(token: string, id: number) {
  return request<{ message: string }>(`/students/${id}`, { method: "DELETE", token });
}

/* ------------------------------------------------------- enrolment requests */

export type CourseRequestState = "none" | "pending" | "rejected" | "enrolled";

export interface CatalogCourse {
  id: number;
  title: string;
  slug: string;
  tagline: string | null;
  summary: string | null;
  category_id: number | null;
  category_name: string | null;
  level: string | null;
  duration: string | null;
  mode: string | null;
  image: string | null;
  accent: string | null;
  request_status: CourseRequestState;
  request_note: string | null;
}

export interface CourseCatalog {
  categories: { id: number; name: string }[];
  courses: CatalogCourse[];
}

export async function apiCourseCatalog(token: string): Promise<CourseCatalog> {
  const data = await request<{ data: CourseCatalog }>("/enrollment-requests/catalog", { token });
  return data.data;
}

export interface MyCourse {
  id: number;
  enrolled_on: string;
  course: { id: number; title: string; slug: string; tagline: string | null; duration: string | null; level: string | null };
  batch: {
    id: number;
    code: string;
    name: string;
    mentor: string | null;
    mode: string;
    timing: string | null;
    start_date: string | null;
    status: "Upcoming" | "Ongoing" | "Completed";
  };
  progress: CourseProgressView;
  /** Staff have uploaded this student's certificate. */
  certificate_ready: boolean;
}

/** A student's progress through a course's modules. */
export interface CourseProgressView {
  modules: string[];
  /** indexes into modules */
  completed: number[];
  percent: number;
  instructions: string;
  completed_at: string | null;
}

export interface BatchProgressData extends CourseProgressView {
  batch: {
    id: number;
    code: string;
    name: string;
    course_title: string | null;
    mentor: string | null;
    mode: string;
    timing: string | null;
    start_date: string | null;
    status: BatchStatusValue;
    seats: number;
    enrolled: number;
  };
  students: { id: number; name: string; email: string; phone: string | null; portal_id: string | null; enrolled_on: string; certificate: StudentCertificate | null }[];
}

export interface StudentCertificate {
  file_name: string;
  file_size: number | null;
  uploaded_at: string;
  uploaded_by: string | null;
}

export async function apiBatchProgress(token: string, batchId: number): Promise<BatchProgressData> {
  const data = await request<{ data: BatchProgressData }>(`/progress/batch/${batchId}`, { token });
  return data.data;
}

export function apiSaveBatchProgress(token: string, batchId: number, input: { completed: number[]; instructions: string }) {
  return request<{ data: CourseProgressView }>(`/progress/batch/${batchId}`, { method: "PUT", body: input, token });
}

/** Student: a short-lived link to the certificate staff uploaded for them. */
export async function apiCertificate(token: string, batchId: number): Promise<{ url: string; file_name: string }> {
  const data = await request<{ data: { url: string; file_name: string } }>(`/progress/certificate/${batchId}`, { token });
  return data.data;
}

/** Staff: upload (or replace) one student's certificate - a PDF or image. */
export function apiUploadCertificate(token: string, batchId: number, userId: number, file: File) {
  const form = new FormData();
  form.append("file", file);
  return request<{ data: StudentCertificate }>(`/progress/batch/${batchId}/certificates/${userId}`, { method: "PUT", form, token });
}

export function apiRemoveCertificate(token: string, batchId: number, userId: number) {
  return request<{ message: string }>(`/progress/batch/${batchId}/certificates/${userId}`, { method: "DELETE", token });
}

export async function apiMyCourses(token: string): Promise<MyCourse[]> {
  const data = await request<{ data: MyCourse[] }>("/enrollment-requests/mine", { token });
  return data.data ?? [];
}

export function apiRequestEnrollment(token: string, courseId: number, message?: string) {
  return request<{ message: string }>("/enrollment-requests", { body: { course_id: courseId, message }, token });
}

export interface EnrollmentRequestRecord {
  id: number;
  status: "pending" | "approved" | "rejected";
  message: string | null;
  decision_note: string | null;
  decided_by: string | null;
  decided_at: string | null;
  createdAt: string;
  /** id is null for a website visitor who has no login yet. */
  student: { id: number | null; name: string; email: string; phone: string | null; portal_id: string | null } | null;
  course: { id: number; title: string; category_name: string | null } | null;
  batch_name: string | null;
  batches: { id: number; code: string; name: string; mode: string; timing: string | null; seats: number; enrolled: number }[];
  /** One entry per course the enquiry asks for, each with its own open batches. */
  courses_batches?: { course_id: number; title: string; batches: { id: number; code: string; name: string; mode: string; timing: string | null; seats: number; enrolled: number }[] }[];
}

/** Result of approving / rejecting an enquiry - `mail_sent` says whether the enquirer's email actually went out. */
export type DecisionResult = { message: string; mail_sent: boolean; mail_to: string | null; mail_note: string | null; account_created?: boolean };

export function apiApproveEnrollment(token: string, id: number, batchIds: number | number[] | null) {
  const ids = Array.isArray(batchIds) ? batchIds : batchIds ? [batchIds] : [];
  return request<DecisionResult>(`/enrollment-requests/${id}/approve`, { method: "PUT", body: { batch_ids: ids }, token });
}

export function apiRejectEnrollment(token: string, id: number, reason: string) {
  return request<DecisionResult>(`/enrollment-requests/${id}/reject`, { method: "PUT", body: { reason }, token });
}
